import { ErrorHandler, inject, Injectable, signal } from '@angular/core';
import { DICTIONARY } from './translation.data';
import { catchError, tap } from 'rxjs/operators';
import { CmsApiService } from '../../services/api/cms-api.service';
import { LanguageService } from '../../services/language/language.service';
import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DataSaverService } from '../../services/data-saver.service';
import { PlatformService } from '../../services/platform.service';
import { MissingTranslateError } from '../errors/missing-translate';
import { CmsContentMapperService } from '../../services/cms-content-mapper.service';
import { isNullOrUndefined } from '../../helpers/utils';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private _cmsApi = inject(CmsApiService);
  private _language = inject(LanguageService);
  private _dataSaver = inject(DataSaverService);
  private _platform = inject(PlatformService);
  private _errorHandler = inject(ErrorHandler);
  private _cmsMapper = inject(CmsContentMapperService);


  public loaded = signal<boolean>(false);

  /**
   * Available languages
   */
  private _dictionary: any = DICTIONARY;

  private _missingTranslates: Set<string> = new Set();
  /**
   * A flag to prevent duplicate error logs.
   */
  private _isTranslateErrorLoaded: boolean = null;

  /**
   * Translate text
   *
   * Using: translate('Hello, #name#', {name: 'Alex'});
   * Output: 'Hei Alex'; // (fi lang)
   *
   * @param key
   * @param params
   */
  public translate(key, params = null): string {
    let translated = '';
    let targetLanguage = this._language.current;

    if (isNullOrUndefined(this._dictionary[targetLanguage])) {
      targetLanguage = this._language.previous;
    }

    if (isNullOrUndefined(this._dictionary[targetLanguage]) || isNullOrUndefined(this._dictionary[targetLanguage][key])) {
      translated = key;

      if (key && !this._missingTranslates.has(key) && DICTIONARY[targetLanguage]  && !this._isTranslateErrorLoaded) {
        this._missingTranslates.add(key);
        this._errorHandler.handleError(new MissingTranslateError(key));
      }
    } else {
      translated = this._cmsMapper.overrideAllUrlsForMirrorsInText(this._dictionary[targetLanguage][key]);
    }

    if (params) {
      const keyParams = [];
      const regex = /\#([^^]*?)\#/ig;
      let match;
      while (match = regex.exec(translated)) {
        keyParams.push(match[1]);
      }
      keyParams.forEach((item) => {
        translated = translated.replace(`#${item}#`, params[item]);
      });
    }

    return translated;
  }

  /**
   * Load user translates
   */
  public loadTranslates() {
    this.loaded.set(false);
    return this._cmsApi.localizationList().pipe(
      catchError(error => of({data: []})),
      tap((res: {data: any[]}) => {
        this._resolveLangFormat(res);
        this.loaded.set(true);
      }),
    );
  }

  /**
   * Resolve lang format
   * @param res
   */
  private _resolveLangFormat(res: any) {
    if (!res.data) {
      return;
    }

    const keyValuePairs = {};
    res.data.forEach((item) => {
      if (!isNullOrUndefined(item.key) && !isNullOrUndefined(item.value)) {
        keyValuePairs[item.key] = item.value;
      }
    });
    DICTIONARY[this._language.current] = keyValuePairs;
  }

  /**
   * Init global functions for usage from console
   *
   * @private
   */
  private _initGlobalFunctions() {
    if (this._platform.isBrowser && !environment.production) {
      window['removeTranslations'] = this._dataSaver.remove.bind(this._dataSaver, 'missing-translations');
      window['extractTranslations'] = () => {
        const translationList = this._dataSaver.extract('missing-translations');
        let output = '';

        translationList.forEach(value => {
          const [ key, langs ] = value.split('----');
          output += key + String.fromCharCode(9) + langs + '\n';
        });

        console.log(output);
      };
    }
  }

  /**
   * Save label without translates to storage
   *
   * @param key
   * @private
   */
  private _collectMissingLangs(key: string) {
    const missingLangs = [];
    const ignoreLangs = ['en'];

    Object.keys(this._dictionary).forEach((lang) => {
      if (!ignoreLangs.includes(lang) && !this._dictionary[lang][key]) {
        missingLangs.push(lang);
      }
    });

    if (missingLangs.length) {
      this._dataSaver.push('missing-translations', key + '----' + missingLangs.join());
    }
  }
}
